import type { ReactNode } from 'react'
import { useEffect } from 'react'

import * as Sentry from '@sentry/react'
import { Analytics } from '@vercel/analytics/react'
import { Settings } from 'luxon'
import ReactGA from 'react-ga4'
import TagManager from 'react-gtm-module'
import { ToastContainer } from 'react-toastify'
import toastr from 'toastr'

import { Toaster } from '@/components/ui/sonner'
import { clientEnv } from '@/env.client'
import { Guard } from '@/features/auth/Guard'
import { resolveAuthPolicy } from '@/features/auth/policy'
import { Hotjar } from '@/shared/lib/integrations/hotjar'
import { initTimeAgo } from '@/shared/lib/integrations/time-ago'
import { Providers } from '@/shared/providers'
import {
  DialogHost,
  RedirectCountdownHost
} from '@/shared/ui/dialog'
import { APP_ROOT_ID } from '@/shared/ui/Layout/dom-ids'
import { VersionProvider } from '@/shared/version'

import 'webrtc-adapter'

toastr.options.positionClass = 'toast-top-center'

Settings.defaultZone = 'America/Sao_Paulo'
Settings.defaultLocale = 'pt-BR'

const isDev = import.meta.env.DEV

type AppShellProps = {
  authPolicy: ReturnType<typeof resolveAuthPolicy>
  children: ReactNode
}

export const AppShell = ({ authPolicy, children }: AppShellProps) => {
  useEffect(() => {
    if (!isDev) ReactGA.initialize('G-4RRDHQK5TM')
  }, [])

  useEffect(() => {
    if (!isDev && clientEnv.NEXT_PUBLIC_EVNTTZ_GTM_ID) {
      TagManager.initialize({ gtmId: clientEnv.NEXT_PUBLIC_EVNTTZ_GTM_ID })
    }

    initTimeAgo()

    if (typeof window.IntersectionObserver === 'undefined') {
      Sentry.captureException(
        new Error(
          'IntersectionObserver is not supported in this browser. Please consider using a modern browser.'
        )
      )
    }
  }, [])

  useEffect(() => {
    const root = document.documentElement
    const body = document.body

    if (root) root.setAttribute('data-bs-theme', 'light')
    if (body) {
      body.classList.add('app-default')
      body.style.fontFamily = 'Inter, system-ui, sans-serif'
      root.setAttribute('id', APP_ROOT_ID)
    }

    return () => {
      if (root) root.removeAttribute('data-bs-theme')
      if (body) {
        body.classList.remove('app-default')
        body.style.removeProperty('font-family')
        root.removeAttribute('id')
      }
    }
  }, [])

  return (
    <main className="flex h-full min-h-0 max-h-[min(100svh,100dvh)] flex-1 flex-col overflow-hidden">
      <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
        <Providers
          session={undefined}
          dehydratedState={undefined}
          initialUserAgent={undefined}
          initialIsIframe={undefined}
        >
          <VersionProvider>
            <Guard
              {...({
                authMode: authPolicy.mode,
                requiresDigitalProductId: authPolicy.requiresDigitalProductId
              } as Record<string, unknown>)}
            >
              {children}
            </Guard>
          </VersionProvider>
        </Providers>
      </div>
      <ToastContainer
        position="top-center"
        autoClose={5000}
        hideProgressBar
        newestOnTop={false}
        closeOnClick
        rtl={false}
        closeButton={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
      />
      <DialogHost />
      <RedirectCountdownHost />
      <Toaster position="top-center" theme="light" richColors closeButton />
      <Analytics />
      <Hotjar />
    </main>
  )
}
